import { supabase } from './supabase';
import { supabaseData } from './supabaseData';

/**
 * Generate recurring job instances from a parent recurring job
 *
 * @param {Object} parentJob - The parent job with recurrence settings
 * @returns {Promise<Object>} { success, instances, error }
 */
export async function generateRecurringInstances(parentJob) {
  if (!parentJob.is_recurring || !parentJob.recurrence_rule || !parentJob.recurrence_end_date) {
    return { success: false, error: 'Job is not configured for recurrence' };
  }

  try {
    const instances = [];
    const startDate = new Date(parentJob.scheduled_date);
    const endDate = new Date(parentJob.recurrence_end_date);

    // Calculate interval based on recurrence rule
    let intervalDays;
    switch (parentJob.recurrence_rule) {
      case 'weekly':
        intervalDays = 7;
        break;
      case 'biweekly':
        intervalDays = 14;
        break;
      case 'monthly':
        intervalDays = 30; // Approximate, will handle month-end cases
        break;
      default:
        intervalDays = 7;
    }

    // Generate instances starting from the second occurrence
    let currentDate = new Date(startDate);

    if (parentJob.recurrence_rule === 'monthly') {
      // For monthly, move to same day next month
      currentDate.setMonth(currentDate.getMonth() + 1);
    } else {
      // For weekly/biweekly, add interval days
      currentDate.setDate(currentDate.getDate() + intervalDays);
    }

    while (currentDate <= endDate) {
      // Generate job number for this instance
      const { data: jobNumberData, error: seqError } = await supabaseData
        .rpc('get_next_job_number');

      let jobNumber;
      if (seqError) {
        // Fallback
        const ts = Date.now();
        jobNumber = `JOB-REC-${parentJob.job_number.replace('JOB-', '')}-${ts}`;
      } else {
        jobNumber = jobNumberData;
      }

      const instanceData = {
        company_id: parentJob.company_id,
        job_number: jobNumber,
        customer_id: parentJob.customer_id,
        quote_id: parentJob.quote_id,
        job_type: parentJob.job_type,
        scheduled_date: currentDate.toISOString().split('T')[0],
        scheduled_time: parentJob.scheduled_time,
        estimated_duration: parentJob.estimated_duration,
        status: 'scheduled',
        quoted_value: parentJob.quoted_value,
        notes: parentJob.notes || null,
        pricing_table_id: parentJob.pricing_table_id,
        deposit_required: parentJob.deposit_required,
        deposit_amount: parentJob.deposit_amount,
        deposit_paid: false,
        // Link to parent
        recurring_parent_id: parentJob.id,
        is_recurring: false // Instances are not themselves recurring
      };

      instances.push(instanceData);

      // Move to next occurrence
      if (parentJob.recurrence_rule === 'monthly') {
        currentDate.setMonth(currentDate.getMonth() + 1);
      } else {
        currentDate.setDate(currentDate.getDate() + intervalDays);
      }
    }

    // Insert all instances
    if (instances.length > 0) {
      const { data, error } = await supabaseData
        .from('jobs')
        .insert(instances)
        .select();

      if (error) throw error;

      return { success: true, instances: data };
    }

    return { success: true, instances: [] };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Get all instances of a recurring job
 *
 * @param {string} parentJobId - UUID of the parent recurring job
 * @returns {Promise<Array>} Array of job instances
 */
export async function getRecurringInstances(parentJobId) {
  try {
    const { data, error } = await supabaseData
      .from('jobs')
      .select('*')
      .eq('recurring_parent_id', parentJobId)
      .order('scheduled_date', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    return [];
  }
}

/**
 * Cancel all future instances of a recurring job
 *
 * @param {string} parentJobId - UUID of the parent recurring job
 * @param {string} fromDate - Cancel instances from this date onwards (optional, defaults to today)
 * @returns {Promise<Object>} { success, cancelled, error }
 */
export async function cancelFutureInstances(parentJobId, fromDate = null) {
  try {
    const cancelFrom = fromDate || new Date().toISOString().split('T')[0];

    const { data, error } = await supabaseData
      .from('jobs')
      .update({ status: 'cancelled' })
      .eq('recurring_parent_id', parentJobId)
      .gte('scheduled_date', cancelFrom)
      .neq('status', 'completed')
      .select();

    if (error) throw error;

    return { success: true, cancelled: data?.length || 0 };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Check if a job is a recurring parent
 *
 * @param {Object} job - Job object
 * @returns {boolean}
 */
export function isRecurringParent(job) {
  return job.is_recurring === true && !job.recurring_parent_id;
}

/**
 * Check if a job is a recurring instance (child)
 *
 * @param {Object} job - Job object
 * @returns {boolean}
 */
export function isRecurringInstance(job) {
  return !!job.recurring_parent_id;
}

/**
 * Format recurrence rule for display
 *
 * @param {string} rule - Recurrence rule (weekly, biweekly, monthly)
 * @returns {string}
 */
export function formatRecurrenceRule(rule) {
  switch (rule) {
    case 'weekly':
      return 'Weekly';
    case 'biweekly':
      return 'Every 2 weeks';
    case 'monthly':
      return 'Monthly';
    default:
      return rule;
  }
}
